import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Search, Star, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FollowedBrandsScreen = () => {
  const { setCurrentScreen } = useApp();
  const [allBrands, setAllBrands] = useState([]);
  const [followedBrands, setFollowedBrands] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Get all brands
      const brandsRes = await fetch(`${BACKEND_URL}/api/brands/list`);
      const brandsData = await brandsRes.json();
      setAllBrands(brandsData.brands || []);

      // Get followed brands
      const followedRes = await fetch(`${BACKEND_URL}/api/auth/brands/following`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const followedData = await followedRes.json();
      setFollowedBrands(followedData.followed_brands?.map(b => b.key) || []);
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFollow = async (brandKey) => {
    const isFollowed = followedBrands.includes(brandKey);
    const token = localStorage.getItem('auth_token');

    try {
      const url = isFollowed
        ? `${BACKEND_URL}/api/auth/brands/unfollow/${brandKey}`
        : `${BACKEND_URL}/api/auth/brands/follow/${brandKey}`;
      
      const response = await fetch(url, {
        method: isFollowed ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        if (isFollowed) {
          setFollowedBrands(followedBrands.filter(b => b !== brandKey));
        } else {
          setFollowedBrands([...followedBrands, brandKey]);
        }
      }
    } catch (error) {
      console.error('Error toggling brand:', error);
    }
  };

  const filteredBrands = allBrands.filter(brand =>
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#1B7340';
    if (rating >= 3) return '#996B3D';
    return '#B85C3B';
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-4">
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <img src="/logos/logo-black.jpg" alt="ATELIA" className="h-24 w-auto object-contain" />
      </div>

      <div className="px-6 pb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2 font-bold">TASTE CURATION</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Followed Brands</h1>
        <p className="text-sm text-black/55 mt-2">{followedBrands.length} brands followed</p>
      </div>

      {/* Search */}
      <div className="px-6 mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-black/55" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SEARCH BRANDS"
            className="w-full pl-12 pr-4 py-3 bg-white border border-black/12 rounded-xl uppercase
              focus:outline-none focus:border-black/30 transition-all placeholder:text-black/30"
          />
        </div>
      </div>

      {/* Brands List */}
      <div className="px-6 space-y-2">
        {loading ? (
          <p className="text-center text-black/55 py-8">Loading brands...</p>
        ) : filteredBrands.length === 0 ? (
          <p className="text-center text-black/55 py-8">No brands found</p>
        ) : (
          filteredBrands.map((brand) => {
            const isFollowed = followedBrands.includes(brand.key);
            return (
              <div
                key={brand.key}
                className="bg-white border border-black/12 rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${getRatingColor(brand.rating)}15` }}
                  >
                    <Star size={16} style={{ color: getRatingColor(brand.rating) }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-black uppercase">{brand.name}</p>
                    <p className="text-xs text-black/55">{brand.rating_label}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleFollow(brand.key)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all ${
                    isFollowed
                      ? 'bg-black text-white'
                      : 'bg-white border border-black/12 text-black hover:bg-black/5'
                  }`}
                >
                  {isFollowed ? (
                    <span className="flex items-center gap-1">
                      <Check size={14} /> Following
                    </span>
                  ) : (
                    'Follow'
                  )}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FollowedBrandsScreen;